import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layers, Loader2, CheckCircle2, AlertTriangle, XCircle, PlayCircle, Download } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall, downloadFile } from '../../utils/api';

const fmtTime = (v) => (v ? new Date(v).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

/** Phase A follow-up — Bulk Assign screen (employers + agencies).
 * Select shifts, pick a worker per row, validate all, assign all. */
export default function BulkAssignPanel({ workersEndpoint = '/api/worker/directory' }) {
  const [me, setMe] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [rows, setRows] = useState({});      // job_id -> worker_id
  const [results, setResults] = useState({}); // job_id -> {blocks, warnings, ok, assigned}
  const [busy, setBusy] = useState('');
  const [force, setForce] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const meRes = await apiCall('/api/auth/me');
      const myId = meRes.user?._id || meRes._id;
      setMe(myId);
      const [jobsRes, workersRes] = await Promise.all([
        apiCall('/api/jobs/search?status=active,draft&limit=200'),
        apiCall(workersEndpoint),
      ]);
      const mine = (jobsRes.jobs || []).filter((j) => j.employer_id === myId && !j.assigned_worker_id);
      setShifts(mine);
      const list = workersRes.workers || workersRes.data || [];
      setWorkers(list.map((w) => ({
        id: w.worker_id || w._id,
        name: w.name || w.full_name || w.worker_name || w.email || 'Worker',
      })).filter((w) => w.id));
    } catch (e) {
      toast.error(e?.message || 'Failed to load shifts/workers');
    } finally { setLoading(false); }
  }, [workersEndpoint]);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => Object.entries(rows).filter(([, w]) => w), [rows]);

  const autoMatch = async () => {
    if (!shifts.length) return;
    setBusy('automatch');
    const picks = { ...rows };
    let matched = 0;
    for (const job of shifts) {
      if (picks[job._id]) continue;
      try {
        const d = await apiCall(`/api/org/shifts/${job._id}/recommendations?limit=1`);
        const best = (d.recommendations || [])[0];
        if (best) {
          picks[job._id] = best.worker_id;
          setResults((prev) => ({
            ...prev,
            [job._id]: { ok: true, blocks: [], warnings: best.warnings || [], matched: best },
          }));
          matched += 1;
        }
      } catch (e) { /* skip row on error */ }
    }
    setRows(picks);
    setBusy('');
    toast[matched ? 'success' : 'warning'](matched ? `Auto-matched ${matched} shift(s) with top-ranked workers` : 'No qualified roster workers found');
  };

  const validateAll = async () => {
    if (!selected.length) return toast.error('Pick a worker for at least one shift');
    setBusy('validate');
    const out = {};
    for (const [jobId, workerId] of selected) {
      try {
        const d = await apiCall(`/api/org/shifts/${jobId}/assignment-check/${workerId}`);
        out[jobId] = { ok: d.ok, blocks: d.blocks || [], warnings: d.warnings || [] };
      } catch (e) {
        out[jobId] = { ok: false, blocks: [e?.message || 'Validation failed'], warnings: [] };
      }
    }
    setResults(out);
    setBusy('');
    const blocked = Object.values(out).filter((r) => r.blocks.length).length;
    toast[blocked ? 'warning' : 'success'](blocked ? `${blocked} row(s) blocked` : 'All rows valid');
  };

  const assignAll = async () => {
    if (!selected.length) return toast.error('Pick a worker for at least one shift');
    setBusy('assign');
    try {
      const d = await apiCall('/api/org/shifts/bulk-assign', {
        method: 'POST',
        body: JSON.stringify({
          assignments: selected.map(([job_id, worker_id]) => ({ job_id, worker_id })),
          force,
        }),
      });
      const out = { ...results };
      d.results.forEach((r) => {
        out[r.job_id] = {
          ok: r.success, assigned: r.success,
          blocks: r.blocks || (r.success ? [] : [r.message || 'Failed']),
          warnings: r.warnings || [],
        };
      });
      setResults(out);
      toast[d.failed ? 'warning' : 'success'](`${d.assigned} assigned · ${d.failed} failed`);
      if (d.assigned) {
        setTimeout(load, 800);
        setRows({});
      }
    } catch (e) {
      toast.error(e?.message || 'Bulk assign failed');
    } finally { setBusy(''); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-indigo-500 animate-spin" /></div>;
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200" data-testid="bulk-assign-panel">
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center gap-3">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Layers className="w-5 h-5 text-indigo-600" /> Bulk Assign
        </h3>
        <span className="text-xs text-gray-400">{shifts.length} unassigned shift{shifts.length === 1 ? '' : 's'} · {workers.length} workers</span>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                await downloadFile('/api/me/shifts/calendar.ics', 'scensob-shifts.ics');
                toast.success('Shift calendar (.ics) downloaded');
              } catch (e) { toast.error(e?.message || 'Calendar export failed'); }
            }}
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 inline-flex items-center gap-1"
            title="Export all org shifts to calendar (.ics)"
            data-testid="org-export-calendar-btn"
          >
            <Download className="w-3.5 h-3.5" /> .ics
          </button>
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)}
              className="rounded border-gray-300" data-testid="bulk-assign-force-checkbox" />
            Override warnings
          </label>
          <button onClick={autoMatch} disabled={!!busy || !shifts.length}
            className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-40 inline-flex items-center gap-1"
            data-testid="bulk-automatch-btn">
            {busy === 'automatch' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />}
            Auto-Match
          </button>
          <button onClick={validateAll} disabled={!!busy || !selected.length}
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 inline-flex items-center gap-1"
            data-testid="bulk-validate-btn">
            {busy === 'validate' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Validate All ({selected.length})
          </button>
          <button onClick={assignAll} disabled={!!busy || !selected.length}
            className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 inline-flex items-center gap-1"
            data-testid="bulk-assign-btn">
            {busy === 'assign' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
            Assign All
          </button>
        </div>
      </div>

      {shifts.length === 0 ? (
        <p className="px-4 py-10 text-sm text-gray-400 text-center">No unassigned shifts. Create or publish shifts first.</p>
      ) : (
        <div className="divide-y divide-gray-50 max-h-[520px] overflow-y-auto">
          {shifts.map((job) => {
            const r = results[job._id];
            return (
              <div key={job._id} className="px-4 py-3" data-testid={`bulk-row-${job._id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[220px]">
                    <p className="text-sm font-medium text-gray-800 truncate">{job.title}</p>
                    <p className="text-[11px] text-gray-400">
                      {fmtTime(job.start_time)} → {fmtTime(job.end_time)} · {job.status}
                      {job.pay_rate ? ` · $${job.pay_rate}/hr` : ''}
                    </p>
                  </div>
                  <select
                    value={rows[job._id] || ''}
                    onChange={(e) => {
                      setRows({ ...rows, [job._id]: e.target.value });
                      setResults((prev) => { const p = { ...prev }; delete p[job._id]; return p; });
                    }}
                    className="w-52 px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                    data-testid={`bulk-worker-select-${job._id}`}
                  >
                    <option value="">— select worker —</option>
                    {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                  {r && r.matched && (
                    <span className="text-[11px] text-violet-600 font-semibold" data-testid={`bulk-match-score-${job._id}`}>
                      ★ {r.matched.name} · {r.matched.score}
                    </span>
                  )}
                  {r && (
                    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full font-semibold ${r.assigned ? 'bg-emerald-50 text-emerald-600' : r.blocks.length ? 'bg-red-50 text-red-600' : r.warnings.length ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}
                      data-testid={`bulk-result-${job._id}`}>
                      {r.assigned ? <><CheckCircle2 className="w-3 h-3" /> Assigned</>
                        : r.blocks.length ? <><XCircle className="w-3 h-3" /> Blocked</>
                          : r.warnings.length ? <><AlertTriangle className="w-3 h-3" /> Warnings</>
                            : <><CheckCircle2 className="w-3 h-3" /> OK</>}
                    </span>
                  )}
                </div>
                {r && (r.blocks.length > 0 || r.warnings.length > 0) && (
                  <div className="mt-1.5 space-y-0.5">
                    {r.blocks.map((b, i) => (
                      <p key={`b${i}`} className="text-[11px] text-red-500 flex items-start gap-1"><XCircle className="w-3 h-3 mt-0.5 shrink-0" />{b}</p>
                    ))}
                    {r.warnings.map((w, i) => (
                      <p key={`w${i}`} className="text-[11px] text-amber-600 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{w}</p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
